import { Building2, CreditCard, Briefcase, ArrowRight, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export type AddItemPreset = { itemType: 'asset' | 'liability'; category?: string };

interface EmptyStateProps {
  onAddItem: (preset?: AddItemPreset) => void;
}

export default function EmptyState({ onAddItem }: EmptyStateProps) {
  return (
    <div className="text-center max-w-2xl mx-auto py-12">
      <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-4">
        Welcome to Your Financial Dashboard
      </h2>
      <p className="text-slate-600 mb-8">
        Let&apos;s get started by adding your first financial item. You can add assets like property
        or investments, and liabilities such as mortgages or loans.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8">
        <Card
          onClick={() => onAddItem({ itemType: 'asset', category: 'real_estate' })}
          role="button"
          tabIndex={0}
          className="cursor-pointer rounded-xl sm:rounded-2xl border border-slate-200/80 shadow-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/20 focus-visible:ring-offset-2"
        >
          <CardContent className="pt-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Property</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Add your home, rental properties, or other real estate assets
            </p>
          </CardContent>
        </Card>

        <Card
          onClick={() => onAddItem({ itemType: 'asset', category: 'investment' })}
          role="button"
          tabIndex={0}
          className="cursor-pointer rounded-xl sm:rounded-2xl border border-slate-200/80 shadow-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/20 focus-visible:ring-offset-2"
        >
          <CardContent className="pt-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Investments</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Track stocks, bonds, retirement accounts, and other investments
            </p>
          </CardContent>
        </Card>

        <Card
          onClick={() => onAddItem({ itemType: 'liability' })}
          role="button"
          tabIndex={0}
          className="cursor-pointer rounded-xl sm:rounded-2xl border border-slate-200/80 shadow-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/20 focus-visible:ring-offset-2"
        >
          <CardContent className="pt-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Liabilities</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Manage mortgages, loans, credit cards, and other debts
            </p>
          </CardContent>
        </Card>
      </div>

      <Button onClick={() => onAddItem()} className="gap-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 font-semibold btn-hover-lift">
        Add Your First Item
        <ArrowRight className="w-5 h-5" />
      </Button>

      <Card className="mt-8 rounded-xl sm:rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
        <CardContent className="pt-6">
          <h4 className="font-medium text-slate-900 mb-2 flex items-center justify-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500 shrink-0" aria-hidden />
            Pro Tip
          </h4>
          <p className="text-sm text-slate-600 leading-relaxed">
            Start with your largest assets and liabilities first, like your home or mortgage.
            You can always add more items later.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
